import { useEffect, useRef, useState } from 'react';
import type { AppealKind, Claim, PacketDoc } from '../types';
import { billedOf, daysLeft, formatDateFull, money, noBillReason } from '../utils';
import { hasMoneyPermission, MONEY_APPROVAL_NOTE } from '@/features/shared/permissions';
import { deadlineLead, useAppealWindows, withAppealWindow } from '../appealWindows';
import { destLabel, isMail, ruleForPayerName } from '../payerRules';
import { resetApproval, signerFor } from '../store';
import { Check, Spinner } from './common';
import { SourceLink, SourceRow, claimSrcContext } from './popovers';
import { ActionArea, ResolvedCard } from './actions';
import type { ActionHandlers } from './actions';
import { Link } from 'react-router-dom';

interface Props {
  claim: Claim;
  handlers: ActionHandlers;
  onDecide: (optionId: string, note: string) => void;
  onPreview: () => void;
  onBack?: () => void;
}

const KIND_CHIP: Record<AppealKind, string> = {
  billing: 'Billing appeal',
  clinical: 'Clinical appeal',
  patient: 'Patient’s appeal',
};

export function DeniedDetail(props: Props) {
  const { claim } = props;
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [claim.id]);

  const billed = billedOf(claim.procedures);
  const procWords = claim.procedures
    .map((p) => `${p.code}${p.tooth ? ` ${p.tooth}` : ''}`)
    .join(' · ');

  return (
    <main className="apl-detail">
      {props.onBack ? (
        <button type="button" className="apl-back" onClick={props.onBack}>
          ← All claims
        </button>
      ) : null}
      <div className="apl-detail-inner">
        <section className="apl-claimhead" aria-labelledby="apl-name">
          <div>
            <h2 id="apl-name" className="apl-patient" tabIndex={-1} ref={headingRef}>
              {claim.patient}
            </h2>
            <p className="apl-chsub">
              {claim.payer.name} · member {claim.memberId} · service {claim.serviceDate} · {claim.provider}
            </p>
            <p className="apl-chmeta">
              {procWords} · claim {claim.id} · denied {claim.deniedDate}
            </p>
          </div>
          <div className="apl-right">
            <div className="apl-meta">Billed</div>
            <div className="apl-bigfig num">{money(billed)}</div>
            <div className="apl-meta">{claim.payer.short} paid $0</div>
          </div>
        </section>

        <section className="apl-card" aria-labelledby="apl-why">
          <span className="apl-eyebrow">{claim.why.eyebrow}</span>
          <h3 className="apl-h3" id="apl-why" style={{ margin: '6px 0 0' }}>
            {claim.why.headline}
          </h3>
          {claim.why.metaLine ? <p className="apl-meta" style={{ margin: '8px 0 0' }}>{claim.why.metaLine}</p> : null}
          {noBillReason(claim) ? <p className="apl-meta" style={{ margin: '8px 0 0' }}>{noBillReason(claim)}</p> : null}
          <SourceRow sources={claim.why.sources} context={claimSrcContext(claim)} />
        </section>

        {claim.answer === 'appeal' ? <AppealRecommendation claim={claim} /> : null}
        {claim.answer === 'resend' ? (
          <section className="apl-inner" aria-label="Recommendation">
            <div className="apl-inner-row">
              <span className="apl-mark ok" aria-hidden="true">
                <Check />
              </span>
              <div>
                <div style={{ fontWeight: 600 }}>{claim.resendReason}</div>
                <p style={{ margin: '4px 0 0' }}>{claim.resendSoft}</p>
                <SourceRow sources={claim.recommendationSources} context={claimSrcContext(claim)} />
              </div>
            </div>
          </section>
        ) : null}
        {claim.answer === 'bill_patient' ? (
          <section className="apl-inner" aria-label="Recommendation">
            <div className="apl-inner-row">
              <span className="apl-mark info" aria-hidden="true">
                i
              </span>
              <div>
                <p style={{ margin: 0 }}>{claim.billSoft}</p>
                {claim.coverageResult ? (
                  <p className="apl-meta" style={{ margin: '8px 0 0', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="apl-mark ok" aria-hidden="true"><Check /></span>
                    {claim.coverageResult}
                  </p>
                ) : null}
                <SourceRow sources={claim.recommendationSources} context={claimSrcContext(claim)} />
              </div>
            </div>
          </section>
        ) : null}
        {claim.answer === 'needs_you' && claim.decision && !claim.decisionRecord && !claim.removed ? (
          <DecisionCard claim={claim} onDecide={props.onDecide} />
        ) : null}
        {claim.decisionRecord && !claim.removed ? <ResolvedCard claim={claim} /> : null}

        {claim.answer === 'appeal' && claim.packet ? <Packet claim={claim} onPreview={props.onPreview} /> : null}
        {claim.answer === 'appeal' && claim.letter ? <LetterCard claim={claim} /> : null}
        {claim.answer === 'appeal' ? <HowElvaSends claim={claim} /> : null}

        <ActionArea claim={claim} h={props.handlers} />

        <p className="apl-history">History: {claim.history.join(' · ')}</p>
      </div>
    </main>
  );
}

export function AppealRecommendation({ claim }: { claim: Claim }) {
  useAppealWindows();
  const d = claim.deadline ? withAppealWindow(claim.deadline, claim.payer) : undefined;
  const chip = claim.appealKind ? KIND_CHIP[claim.appealKind] : null;
  return (
    <section className="apl-rec" aria-labelledby="apl-rec-title">
      <div className="apl-rec-head">
        <h3 className="apl-rec-title" id="apl-rec-title">
          {claim.recommendationHeader}
          {chip ? <span className="apl-chip">{chip}</span> : null}
        </h3>
        {d ? (
          <span className="apl-rec-deadline">
            {deadlineLead(d)} {formatDateFull(d.date)} · {daysLeft(d.date)} days left · {d.basis}
          </span>
        ) : null}
      </div>
      <div className="apl-rec-body">
        <p style={{ margin: 0 }}>{claim.recommendationBody}</p>
        <SourceRow sources={claim.recommendationSources} context={claimSrcContext(claim)} />
      </div>
    </section>
  );
}

/**
 * Needs you (rcm-30 §5): the payer's claim turns on a fact only the patient knows.
 * Staff ask her; ELVA never texts her (D153). The primary changes with the answer.
 */
function DecisionCard({ claim, onDecide }: { claim: Claim; onDecide: (optionId: string, note: string) => void }) {
  const decision = claim.decision;
  const [chosen, setChosen] = useState('');
  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);
  // D245: an answer that ends in a write-off is hidden without the money permission.
  const moneyOk = hasMoneyPermission();
  if (!decision) return null;
  const opt = decision.options.find((o) => o.id === chosen);
  const amount = money(billedOf(claim.procedures));
  const needsNote = opt?.needsNote === true;
  const ready = opt != null && (!needsNote || note.trim() !== '');
  const label = (() => {
    if (!opt) return 'Choose an answer';
    if (opt.resolvesTo === 'write_off') return `Write off ${amount}`;
    return 'Prepare the appeal';
  })();
  return (
    <section className="apl-decision" aria-labelledby="apl-dec-title">
      <div className="apl-decision-head">
        <span className="apl-badge b-white" style={{ background: '#fff', border: '1px solid #e9c98a', color: 'var(--warning)' }}>
          Needs you
        </span>
        <h3 className="apl-decision-title" id="apl-dec-title">
          {decision.title}
        </h3>
      </div>
      <div className="apl-decision-body">
        <p style={{ margin: 0, color: 'var(--fg2)' }}>{decision.body}</p>
        <div className="apl-askbox">
          <div>
            <strong>Ask {claim.firstName}:</strong> {decision.askQuestion}
          </div>
          <div className="apl-askbox-foot">
            <span className="apl-mono">{decision.askPhone}</span>
            <button
              type="button"
              className="apl-act-link"
              onClick={() => {
                void navigator.clipboard.writeText(`Ask ${claim.firstName}: ${decision.askQuestion} ${decision.askPhone}`);
                setCopied(true);
              }}
            >
              {copied ? 'Copied' : 'Copy the question'}
            </button>
          </div>
        </div>
        <p style={{ margin: 0, fontWeight: 600 }}>What did {claim.firstName} say?</p>
        <div className="apl-radio-list" role="radiogroup" aria-labelledby="apl-dec-title">
          {decision.options.filter((o) => moneyOk || o.resolvesTo !== 'write_off').map((o) => (
            <label key={o.id} className="apl-radio apl-radio-block">
              <input
                type="radio"
                name="apl-decision"
                checked={chosen === o.id}
                onChange={() => { setChosen(o.id); }}
              />
              <span>
                <span className="apl-radio-label">{o.label}</span>
                <span className="apl-radio-hint">{o.hint}</span>
              </span>
            </label>
          ))}
        </div>
        {!moneyOk ? <p className="apl-meta" style={{ margin: 0 }}>{MONEY_APPROVAL_NOTE}</p> : null}
        {needsNote ? (
          <textarea
            className="apl-textarea"
            aria-label="Note"
            placeholder="Required: what did she say?"
            value={note}
            onChange={(e) => { setNote(e.target.value); }}
          />
        ) : null}
        <div className="apl-act-row">
          <button
            type="button"
            className="apl-btn apl-btn-primary apl-btn-lg"
            disabled={!ready}
            onClick={() => { if (opt) onDecide(opt.id, note.trim()); }}
          >
            {label}
          </button>
          <span className="apl-actionbar-meta">The button changes with the answer.</span>
        </div>
        <SourceRow sources={claim.recommendationSources} context={claimSrcContext(claim)} />
      </div>
    </section>
  );
}

/** Origin line for the signed documents, reflecting who signs (D130). */
function originFor(doc: PacketDoc, claim: Claim): string {
  const signer = signerFor(claim);
  if (!doc.needsSignature) return doc.origin;
  const verb = doc.isLetter ? 'Written by ELVA' : 'Filled in by ELVA';
  if (claim.appealKind === 'clinical') {
    if (claim.approval === 'approved') {
      return `${verb} · approved by ${signer} on ${claim.approvedOn ?? 'Oct 14'} · signed with ${signer}’s signature on file`;
    }
    return `Waiting for ${signer} to approve · ELVA texted ${signer} ${claim.approvedOn ?? 'Oct 14'}`;
  }
  // billing or patient's: signed for the practice
  return `${verb} · signed with ${signer}’s signature on file`;
}

export function Packet({ claim, onPreview }: { claim: Claim; onPreview: () => void }) {
  const base = claim.packet ?? [];
  const docs: PacketDoc[] = [...base];
  if (claim.appealKind === 'patient') {
    const n = docs.length + 1;
    const origin =
      claim.patientForm === 'signed'
        ? `Signed by ${claim.firstName} on her phone, ${claim.patientFormDate ?? 'Oct 6'}`
        : `Waiting for ${claim.firstName} to sign · ELVA texted her ${claim.patientFormDate ?? 'Oct 14'}`;
    docs.push({ n, name: `${claim.payer.short}’s authorization form`, origin, pages: 1 });
  }
  const pages = docs.reduce((s, d) => s + d.pages, 0);
  return (
    <section className="apl-card" aria-labelledby="apl-packet">
      <div className="apl-card-head" style={{ marginBottom: 6 }}>
        <h3 className="apl-h3" id="apl-packet">
          The appeal packet · {docs.length} documents · {pages} pages
        </h3>
      </div>
      <p className="apl-meta" style={{ marginTop: 0 }}>ELVA put it together. Add anything else you want {claim.payer.short} to see.</p>
      {docs.map((d) => (
        <div key={d.n} className="apl-doc">
          <span className="apl-dn" aria-hidden="true">{d.n}</span>
          <div className="apl-doc-main">
            <div className="apl-doc-name">{d.name}</div>
            <div className="apl-doc-origin">{originFor(d, claim)}</div>
          </div>
          {d.isLetter ? (
            <button type="button" className="apl-btn apl-btn-ghost apl-btn-sm" onClick={() => {
              const el = document.getElementById('apl-letter');
              el?.scrollIntoView({ behavior: 'smooth' });
            }}>
              Read and edit
            </button>
          ) : (
            <button type="button" className="apl-btn apl-btn-ghost apl-btn-sm" onClick={onPreview}>View</button>
          )}
        </div>
      ))}
      <div style={{ marginTop: 10 }}>
        <button type="button" className="apl-btn apl-btn-ghost apl-btn-sm" onClick={onPreview}>
          Add a document
        </button>
      </div>
    </section>
  );
}

export function LetterCard({ claim }: { claim: Claim }) {
  const [text, setText] = useState(claim.letter?.text ?? '');
  const [state, setState] = useState<'elva' | 'edited' | 'rewritten'>('elva');
  const [rewriting, setRewriting] = useState(false);
  const didReset = useRef(false);
  useEffect(() => {
    setText(claim.letter?.text ?? '');
    setState('elva');
    didReset.current = false;
  }, [claim.id, claim.letter?.text]);

  const rewrite = async () => {
    setRewriting(true);
    await new Promise((r) => setTimeout(r, 800));
    setText(claim.letter?.text ?? '');
    setState('rewritten');
    setRewriting(false);
  };

  const onEdit = (value: string) => {
    setText(value);
    setState('edited');
    // Editing a clinical letter after approval sends it back to the dentist (D130).
    if (claim.appealKind === 'clinical' && claim.approval === 'approved' && !didReset.current) {
      didReset.current = true;
      resetApproval(claim.id);
    }
  };

  const signer = signerFor(claim);
  let origin: string;
  if (state === 'rewritten') {
    origin = 'Rewritten by ELVA just now.';
  } else if (state === 'edited') {
    origin =
      claim.appealKind === 'clinical'
        ? `Edited just now — ELVA sent it back to ${signer} to approve again.`
        : 'Edited by Ava Thompson · ELVA’s draft is kept in the history.';
  } else {
    const approvalLine =
      claim.appealKind === 'clinical'
        ? ` ${signer} approved this wording on ${claim.approvedOn ?? 'Oct 14'}; if anyone changes it, ELVA asks ${signer} again.`
        : '';
    origin = `Written by ELVA from ${claim.letter?.basis ?? 'the record'}. The sent letter adds the practice letterhead, ${claim.payer.short}’s address, the patient and claim details, and ${signer}’s signature.${approvalLine}`;
  }

  return (
    <section className="apl-card" id="apl-letter" aria-labelledby="apl-letter-title">
      <div className="apl-card-head">
        <h3 className="apl-h3" id="apl-letter-title">Appeal letter</h3>
        <button type="button" className="apl-btn apl-btn-ghost apl-btn-sm" disabled={rewriting} onClick={() => { void rewrite(); }}>
          {rewriting ? <Spinner label="Rewriting…" /> : 'Rewrite'}
        </button>
      </div>
      <label className="apl-visually-hidden" htmlFor="apl-letter-text">Appeal letter</label>
      <textarea
        id="apl-letter-text"
        className="apl-textarea"
        value={text}
        onChange={(e) => { onEdit(e.target.value); }}
      />
      <div className="apl-origin">{origin}</div>
    </section>
  );
}

function whoSignsLine(claim: Claim): { text: string; done: boolean } {
  const signer = signerFor(claim);
  if (claim.appealKind === 'clinical') {
    if (claim.approval === 'approved') {
      return {
        text: `${signer} approved the letter on ${claim.approvedOn ?? 'Oct 14'} — the treating dentist reads a clinical appeal first. The signature on file goes on the letter and the ${claim.payer.short} form.`,
        done: true,
      };
    }
    return { text: `Waiting for ${signer} to approve — ELVA texted ${signer} the letter.`, done: false };
  }
  if (claim.appealKind === 'patient') {
    if (claim.patientForm === 'signed') {
      return { text: `${claim.firstName} signed ${claim.payer.short}’s authorization form, ${claim.patientFormDate ?? 'Oct 6'}.`, done: true };
    }
    return { text: `Waiting for ${claim.firstName} to sign ${claim.payer.short}’s form — ELVA texted her ${claim.patientFormDate ?? 'Oct 14'}.`, done: false };
  }
  return { text: `Signed for the practice by ${signer}, signature on file.`, done: true };
}

export function HowElvaSends({ claim }: { claim: Claim }) {
  const rule = ruleForPayerName(claim.payer.name);
  const first = rule.order[0] ?? rule.accepts[0] ?? 'certified_mail';
  const label = destLabel(first);
  const isWeb = first === 'website' || first === 'api';
  const mail = isMail(first);
  const who = whoSignsLine(claim);

  // Line 1: the channel and why
  let line1: React.ReactNode;
  if (rule.firstStep) {
    line1 = (
      <div>
        <strong>{claim.payer.short} needs {rule.firstStep.toLowerCase()}</strong>{' '}
        <span style={{ color: 'var(--fg2)' }}>ELVA asks for the adjustment first; it files the dispute if {claim.payer.short} says no.</span>
      </div>
    );
  } else if (isWeb) {
    line1 = (
      <div>
        <strong>{rule.firstDestName} first</strong>{' '}
        <span style={{ color: 'var(--fg2)' }}>— the fastest way {claim.payer.short} accepts. {claim.payer.short} has it the same day and gives a confirmation number, which ELVA saves here.</span>
      </div>
    );
  } else if (first === 'secure_message') {
    line1 = (
      <div>
        <strong>{rule.firstDestName} first</strong>{' '}
        <span style={{ color: 'var(--fg2)' }}>— the fastest way {claim.payer.short} accepts. {claim.payer.short} confirms it has it.</span>
      </div>
    );
  } else if (first === 'fax') {
    line1 = (
      <div>
        <strong>Fax to {claim.payer.short}’s appeals fax</strong>{' '}
        <span style={{ color: 'var(--fg2)' }}>— the fastest way {claim.payer.short} accepts an appeal.</span>
      </div>
    );
  } else if (first === 'secure_email') {
    line1 = (
      <div>
        <strong>{claim.payer.short}’s secure email</strong>{' '}
        <span style={{ color: 'var(--fg2)' }}>— {claim.payer.short} takes reconsiderations this way.</span>
      </div>
    );
  } else {
    line1 = (
      <div>
        <strong>Certified mail with return receipt</strong>{' '}
        <span style={{ color: 'var(--fg2)' }}>— {claim.payer.short} takes appeals by mail only, so ELVA keeps proof of the date.</span>
      </div>
    );
  }

  // Line 3: fallback
  const rest = rule.order.slice(1);
  const fallbackNames = rest.map((ch) => {
    if (ch === 'fax') return 'fax';
    if (ch === 'certified_mail') return 'certified mail with return receipt';
    if (ch === 'certified_mail_plain') return 'certified mail';
    if (ch === 'first_class') return 'first-class mail';
    if (ch === 'secure_message') return rule.firstDestName;
    return ch;
  });
  const fallbackText =
    fallbackNames.length > 0
      ? `If ${isWeb ? 'the website' : 'that'} is down, ${fallbackNames.join(', then ')}. ELVA never sends the same appeal twice.`
      : 'ELVA never sends the same appeal twice.';

  return (
    <section className="apl-card" aria-labelledby="apl-send">
      <h3 className="apl-h3" id="apl-send" style={{ marginBottom: 12 }}>How ELVA sends it</h3>
      {claim.needsHuman ? (
        <div className="apl-warnline" role="note">
          The deadline is close and {claim.payer.short} takes mail only, so mail may reach {claim.payer.short} after the deadline. This appeal is with Human in the loop.
        </div>
      ) : null}
      <div className="apl-send">
        <div className="apl-addr">
          <div className="apl-addr-label">{label}</div>
          <div className="apl-addr-body" style={{ fontWeight: 600, fontSize: 15 }}>
            {rule.firstDestName}
            {rule.firstDestDetail ? <div style={{ fontWeight: 500, color: 'var(--fg2)' }}>{rule.firstDestDetail}</div> : null}
          </div>
          {mail && rule.address === null ? (
            <p className="apl-meta" style={{ marginTop: 6 }}>Use the address on {claim.payer.short}’s denial letter.</p>
          ) : null}
          {isWeb ? <p className="apl-meta" style={{ marginTop: 4 }}>ELVA signs in with your practice’s {claim.payer.short} login.</p> : null}
          <div style={{ marginTop: 8 }}>
            <SourceLink
              source={{ id: 'ev-addr', label: `${rule.source} · checked ${rule.checkedOn}` }}
              context={claimSrcContext(claim)}
            />
          </div>
        </div>
        <ul className="apl-steps">
          <li className="apl-step"><span className="apl-step-n">1</span>{line1}</li>
          <li className="apl-step">
            <span className={who.done ? 'apl-mark ok' : 'apl-step-n'} aria-hidden="true">{who.done ? <Check /> : '2'}</span>
            <div style={{ color: who.done ? 'inherit' : 'var(--fg2)' }}>{who.text}</div>
          </li>
          <li className="apl-step"><span className="apl-step-n">3</span><div style={{ color: 'var(--fg2)' }}>{fallbackText}</div></li>
        </ul>
      </div>
      <p className="apl-meta" style={{ marginTop: 12 }}>
        {claim.payer.short} accepts appeals by {rule.acceptsWords.toLowerCase()}; change the order in{' '}
        <Link to="/settings/appeals" className="apl-src" style={{ textDecoration: 'underline' }}>Settings › Appeals</Link>.
      </p>
    </section>
  );
}
